import { faListCheck, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Button, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAppSelector } from 'src/app/hooks';
import { setIsDirty, setOpenWarnModal } from 'src/app/reducers/manage-room.reducer';
import { store } from 'src/app/store';
import { useFieldArray, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useDeleteManageRoomTaskMutation, useGetManageRoomTasksQuery, useGetManageTaskMaterialQuery, useSaveTaskMutation, useUpdateManageRoomTasksOrderMutation } from '../../manage-room.slice';
import type { ManageRoomDetail } from '../../manage-room.types';
import { DiscardChangesModal } from '../discard-changes-modal';
import { ModalLayout } from '../modal';
import { UnSavedWarnModal } from '../unsaved-warn-modal';
import { TaskAccordion, TaskInfoModal } from './components';
import { taskFormInitialValues } from './task-tab.constants';
import { StyledHeaderHeadingWrapper, StyledHeaderOptionButton, StyledLoadingSpinner, StyledOptionButtonIcon, StyledTaskHeaderHeading, StyledTaskHeaderOptionWrapper, StyledTaskIcon, StyledTaskOptionsButtonContainer, StyledTasksContentContainer, StyledTasksHeader } from './tasks-tab.styles';
export const Tasks = ({
  roomDetails,
  isContentDev
}: {
  roomDetails: ManageRoomDetail;
  isContentDev: boolean;
}) => {
  const roomId = roomDetails?._id;
  const roomCode = roomDetails?.code;
  const {
    isDirty
  } = useAppSelector(state => state.manageRoom);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [isTaskOrderUpdated, setIsTaskOrderUpdated] = useState<boolean>(false);
  const [openUpdateOrderModal, setOpenUpdateOrderModal] = useState<boolean>(false);
  const {
    data: materialData = {}
  } = useGetManageTaskMaterialQuery(roomId);
  const {
    data: tasksData
  } = useGetManageRoomTasksQuery(roomCode);
  const [removeTask] = useDeleteManageRoomTaskMutation();
  const [updateTaskOrder, {
    isLoading: isUpdatingOrder
  }] = useUpdateManageRoomTasksOrderMutation();
  const [saveTask, {
    isLoading
  }] = useSaveTaskMutation();
  const {
    control,
    reset,
    formState: {
      isDirty: isMainFormDirty
    },
    getValues
  } = useForm({
    defaultValues: {
      tasks: tasksData ?? []
    }
  });
  const {
    fields,
    remove,
    move
  } = useFieldArray({
    control,
    name: 'tasks'
  });
  useEffect(() => {
    reset({
      tasks: tasksData
    });
  }, [reset, tasksData]);
  useEffect(() => {
    if (!activeAccordion) {
      store.dispatch(setIsDirty(isMainFormDirty));
    }
  }, [activeAccordion, isMainFormDirty]);
  const handleActiveAccordion = (taskNo: number) => {
    if (isDirty || isMainFormDirty) {
      store.dispatch(setOpenWarnModal(true));
      return;
    }
    setActiveAccordion(prev => prev === taskNo ? null : taskNo);
  };
  const handleAddTask = async () => {
    if (isDirty || isMainFormDirty) {
      store.dispatch(setOpenWarnModal(true));
      return;
    }
    const newTaskNo = fields.length + 1;
    const payload = {
      ...taskFormInitialValues,
      taskNo: newTaskNo,
      roomId,
      roomCode
    };
    try {
      await saveTask(payload).unwrap();
      setActiveAccordion(newTaskNo);
      NotificationDispatch('success', 'Created task for room. All users will be allocated this task along with its questions.');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      } else {
        NotificationDispatch('error', 'Something went wrong');
      }
    }
  };
  const handleRemoveTask = async (index: number, taskId: string) => {
    if (!taskId) {
      remove(index);
      setActiveAccordion(null);
      return;
    }
    try {
      await removeTask({
        roomCode,
        taskId
      }).unwrap();
      setActiveAccordion(null);
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      }
    }
  };
  const findTask = useCallback((fieldId: string) => {
    const task = fields.find(f => f.id === fieldId);
    if (task) {
      return fields.indexOf(task);
    }
    return 0;
  }, [fields]);
  const handleMoveTask = useCallback((dragIndex: number, atIndex: number) => {
    move(dragIndex, atIndex);
  }, [move]);
  const handleTaskOrderUpdate = useCallback(() => {
    if (isMainFormDirty) {
      setIsTaskOrderUpdated(true);
    } else {
      setIsTaskOrderUpdated(false);
    }
  }, [isMainFormDirty]);
  const handleUpdateTaskOrder = async () => {
    const {
      tasks = []
    } = getValues();
    const payload = {
      roomCode,
      tasks: tasks.map(task => ({
        taskId: task._id,
        order: task.taskNo
      }))
    };
    try {
      await updateTaskOrder(payload);
      NotificationDispatch('success', 'Tasks order successfully updated');
      setIsTaskOrderUpdated(false);
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      } else {
        NotificationDispatch('error', 'something went wrong');
      }
    } finally {
      setOpenUpdateOrderModal(false);
    }
  };
  const handleDiscardTaskChanges = () => {
    reset();
    setIsTaskOrderUpdated(false);
  };
  const isUpdateOrderButtonDisabled = !isMainFormDirty && !isTaskOrderUpdated;
  return <section aria-label="Tasks Tab" data-sentry-component="Tasks" data-sentry-source-file="tasks-tab.tsx">
      <StyledTasksHeader data-sentry-element="StyledTasksHeader" data-sentry-source-file="tasks-tab.tsx">
        <StyledHeaderHeadingWrapper data-sentry-element="StyledHeaderHeadingWrapper" data-sentry-source-file="tasks-tab.tsx">
          <StyledTaskIcon icon={faListCheck} data-sentry-element="StyledTaskIcon" data-sentry-source-file="tasks-tab.tsx" />
          <StyledTaskHeaderHeading data-sentry-element="StyledTaskHeaderHeading" data-sentry-source-file="tasks-tab.tsx">Room tasks</StyledTaskHeaderHeading>
        </StyledHeaderHeadingWrapper>

        <StyledTaskHeaderOptionWrapper data-sentry-element="StyledTaskHeaderOptionWrapper" data-sentry-source-file="tasks-tab.tsx">
          <StyledHeaderOptionButton variant="outlined" color="secondary"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleAddTask} disabled={isLoading} data-sentry-element="StyledHeaderOptionButton" data-sentry-source-file="tasks-tab.tsx">
            <StyledOptionButtonIcon icon={faPlus} data-sentry-element="StyledOptionButtonIcon" data-sentry-source-file="tasks-tab.tsx" />
            Add task
          </StyledHeaderOptionButton>

          <TaskInfoModal data-sentry-element="TaskInfoModal" data-sentry-source-file="tasks-tab.tsx" />
        </StyledTaskHeaderOptionWrapper>
      </StyledTasksHeader>

      <StyledTasksContentContainer data-sentry-element="StyledTasksContentContainer" data-sentry-source-file="tasks-tab.tsx">
        {fields.map((field, index) => <TaskAccordion key={field.id} index={index} taskNo={field.taskNo} fieldId={field.id} roomId={roomId} task={field} activeAccordion={activeAccordion} setActiveAccordion={handleActiveAccordion} taskMaterialData={materialData}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleRemoveTask={taskId => handleRemoveTask(index, taskId)} handleMoveTask={handleMoveTask} findTask={findTask} handleTaskOrderUpdate={handleTaskOrderUpdate} roomDetails={roomDetails} isContentDev={isContentDev} />)}
      </StyledTasksContentContainer>

      {fields.length > 1 && !activeAccordion && <StyledTaskOptionsButtonContainer>
          {isTaskOrderUpdated && <DiscardChangesModal handleActionButton={handleDiscardTaskChanges} />}

          <ModalLayout open={openUpdateOrderModal} openChange={setOpenUpdateOrderModal} showWarnBox={false} triggerText="Update order" modalTitleText="Update order" isTriggerButtonDisabled={isUpdateOrderButtonDisabled} closeButtonText="Cancel"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      actionElement={
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      <Button onClick={handleUpdateTaskOrder} disabled={isUpdatingOrder}>
                {isUpdatingOrder && <StyledLoadingSpinner color="#151C2B" />}
                {isUpdatingOrder ? 'Updating order...' : 'Update order'}
              </Button>}>
            <p>Are you sure you want to update the task order?</p>
          </ModalLayout>
        </StyledTaskOptionsButtonContainer>}

      {!activeAccordion && <UnSavedWarnModal isSavingChanges={isUpdatingOrder}
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleActionButton={handleUpdateTaskOrder} handleDiscardChanges={handleDiscardTaskChanges} />}
    </section>;
};
export const TasksTab = ({
  roomDetails,
  isContentDev
}: {
  roomDetails: ManageRoomDetail;
  isContentDev: boolean;
}) => <DndProvider backend={HTML5Backend} data-sentry-element="DndProvider" data-sentry-component="TasksTab" data-sentry-source-file="tasks-tab.tsx">
    <Tasks roomDetails={roomDetails} isContentDev={isContentDev} data-sentry-element="Tasks" data-sentry-source-file="tasks-tab.tsx" />
  </DndProvider>;